import React, { useEffect, useState } from 'react';
import { Button, Col, InputNumber, Modal, PageHeader, Row, Space, Statistic, Table, Tag, Tooltip, message } from 'antd';
import { PoolCounts, PoolWallet, WalletPoolProvider } from '../../domain/usdt-providers/walletPoolProvider';
import { Environment } from '../../core/configs/services';
import _module from '../configs/module';
import { WRITE_SYSTEM_SETTING } from '../configs/scopes';
import WalletPoolCheckoutModal from '../components/WalletPoolCheckoutModal';

const errMsg = (e: any) =>
  e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '失敗';

function tronscanAddressUrl(address: string): string {
  const env = Environment() ?? 'develop';
  const base = env === 'prod' || env === 'stg' ? 'https://tronscan.org/#/address/' : 'https://shasta.tronscan.org/#/address/';
  return base + address;
}

const statusMap: Record<string, { color: string; text: string }> = {
  INCUBATING: { color: 'default', text: '孵化中' },
  READY: { color: 'success', text: '就緒' },
  CLAIMED: { color: 'processing', text: '已領用' },
};

// 狀態欄排序權重:未領用(就緒 → 孵化中)排前面、已領用排後面
const statusRank: Record<string, number> = { READY: 0, INCUBATING: 1, CLAIMED: 2 };
const rankOf = (s: string) => statusRank[s] ?? 9;

/**
 * 預備錢包池後台面板。
 *
 * 佈局照 TrxSweepManager(PageHeader + Statistic 列 + Table),勾選/批次按鈕照
 * AbandonWalletPage(plain rowSelection,不必上 CoreProTable ——這頁資料量小,
 * 一次把 list 全撈回來,不需要伺服器端分頁)。
 */
const WalletPoolManager = (props: { title: string }) => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<PoolCounts | null>(null);
  const [items, setItems] = useState<PoolWallet[]>([]);
  const [selected, setSelected] = useState<PoolWallet[]>([]);
  const [activating, setActivating] = useState(false);
  const [checkoutWallet, setCheckoutWallet] = useState<PoolWallet | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const canWrite = _module.can([WRITE_SYSTEM_SETTING]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await WalletPoolProvider.list();
      setCounts(r.counts);
      setItems(r.items);
    } catch (e: any) {
      message.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const clearAndRefresh = () => {
    setSelected([]);
    load();
  };

  const openCheckout = (wallet: PoolWallet) => {
    setCheckoutWallet(wallet);
    setCheckoutOpen(true);
  };

  const closeCheckout = () => {
    setCheckoutOpen(false);
    setCheckoutWallet(null);
  };

  const doReplenish = () => {
    // 閉包收 InputNumber 值(Modal.confirm 內嵌表單的 repo 慣例,同 UserWalletPage.doDisable)
    let count = Math.max(1, Math.min(100, (counts ? 100 - (counts.incubating + counts.ready) : 100)));
    Modal.confirm({
      title: '手動補貨',
      content: (
        <div>
          <p>mint 新錢包進池(只產金鑰、不上鏈開通,零鏈上成本),最多補到池子目標量。</p>
          <InputNumber min={1} max={100} defaultValue={count} onChange={(v) => { count = Number(v) || 1; }} />
          <span style={{ marginLeft: 8 }}>顆</span>
        </div>
      ),
      okText: '補貨',
      cancelText: '取消',
      onOk: async () => {
        const r = await WalletPoolProvider.replenish(count);
        message.success(`已補 ${r.minted} 顆` + (r.failed ? `,失敗 ${r.failed} 顆` : ''));
        load();
      },
    });
  };

  const doActivate = () => {
    const ids = selected.map((w) => w.id);
    if (!ids.length) return;
    Modal.confirm({
      title: `鏈上開通 ${ids.length} 顆錢包?`,
      content: '每顆花費 RENTAL 1 TRX,開通後不可逆。',
      okText: '確認開通',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setActivating(true);
        try {
          const r = await WalletPoolProvider.activate(ids);
          message.success(`已標記 ${r.marked} 顆進入開通流程`);
          clearAndRefresh();
        } catch (e: any) {
          message.error(errMsg(e));
        } finally {
          setActivating(false);
        }
      },
    });
  };

  const total = items.length;
  const unactivated = counts ? total - counts.activated : 0;

  const columns = [
    {
      title: '地址',
      dataIndex: 'address',
      align: 'center' as const,
      ellipsis: true,
      render: (v: string) => (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href={tronscanAddressUrl(v)} target="_blank" rel="noreferrer">
          {v}
        </a>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      align: 'center' as const,
      // 預設升冪 = 未領用優先;同狀態維持後端 id DESC(Array.sort 穩定)
      sorter: (a: PoolWallet, b: PoolWallet) => rankOf(a.status) - rankOf(b.status),
      defaultSortOrder: 'ascend' as const,
      sortDirections: ['ascend', 'descend', 'ascend'] as any,
      render: (v: string) => {
        const m = statusMap[v] ?? { color: 'default', text: v };
        return <Tag color={m.color}>{m.text}</Tag>;
      },
    },
    {
      title: '開通狀態',
      dataIndex: 'activatedAt',
      align: 'center' as const,
      render: (v: string | null, row: PoolWallet) => {
        if (v) return <Tag color="success">已開通</Tag>;
        if (row.activateRequested === 1) return <Tag color="processing">開通中</Tag>;
        return <Tag color="default">未開通</Tag>;
      },
    },
    {
      title: '領用資訊',
      dataIndex: 'claimedType',
      align: 'center' as const,
      render: (v: string | null, row: PoolWallet) =>
        row.claimedAt ? (
          <Tooltip title={row.claimedAt}>
            <span>
              {v || '-'} · {row.claimedBy || '-'}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, row: PoolWallet) =>
        row.status === 'CLAIMED' ? null : (
          <Button size="small" disabled={!canWrite} onClick={() => openCheckout(row)}>
            取出
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={props.title}
        subTitle=""
        extra={
          <Space>
            <Button loading={loading} onClick={load}>
              重新整理
            </Button>
            <Button disabled={!canWrite} onClick={doReplenish}>
              手動補貨
            </Button>
            <Button
              type="primary"
              disabled={!canWrite || !selected.length}
              loading={activating}
              onClick={doActivate}
            >
              鏈上開通({selected.length})
            </Button>
          </Space>
        }
      />
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic title="孵化中" value={counts?.incubating ?? 0} />
          </Col>
          <Col span={4}>
            <Statistic title="就緒" value={counts?.ready ?? 0} valueStyle={{ color: '#1890ff' }} />
          </Col>
          <Col span={4}>
            <Statistic title="已領用" value={counts?.claimed ?? 0} />
          </Col>
          <Col span={4}>
            <Statistic title="已開通" value={counts?.activated ?? 0} valueStyle={{ color: '#3f8600' }} />
          </Col>
          <Col span={4}>
            <Statistic title="未開通" value={unactivated} />
          </Col>
          <Col span={4}>
            <Statistic
              title="領用孤兒"
              value={counts?.orphanClaimed ?? 0}
              valueStyle={counts && counts.orphanClaimed > 0 ? { color: '#cf1322' } : undefined}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns as any}
          // pageSize 寫死會變受控值,切換 條/頁 沒反應 —— 用 defaultPageSize 讓 antd 自管
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 顆` }}
          rowSelection={{
            selectedRowKeys: selected.map((w) => w.id),
            onChange: (_keys: any, rows: PoolWallet[]) => setSelected(rows),
            getCheckboxProps: (row: PoolWallet) => ({ disabled: !!row.activatedAt }),
          }}
        />
      </Space>

      <WalletPoolCheckoutModal
        open={checkoutOpen}
        wallet={checkoutWallet}
        onClose={closeCheckout}
        onDone={clearAndRefresh}
      />
    </>
  );
};

export default WalletPoolManager;
